import { createApi, fetchBaseQuery, BaseQueryFn } from "@reduxjs/toolkit/query/react";
import { getApiConfig } from "@/config/api";
import { getRequestLanguage } from "@/utils/requestLanguage";
import type { ApiResponse, BackendPaginatedResponse, User } from "@/types";
import { subscriptionApi } from "./subscriptionApi";

export interface UsersQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
  permission?: string;
  createdBy?: string;
  sortBy?: "name" | "username" | "createdAt" | "lastLoginAt";
  sortOrder?: "asc" | "desc";
  storeId?: string;
}

export interface CreateUserData {
  name: string;
  username: string;
  email?: string;
  phoneNumber?: string;
  password: string;
  permissions: string[];
  isActive?: boolean;
}

export interface UpdateUserData {
  name?: string;
  email?: string;
  phoneNumber?: string;
  isActive?: boolean;
}

export interface UpdatePasswordData {
  userId: string;
  newPassword: string;
}

export interface GrantPermissionsData {
  userId: string;
  permissions: string[];
}

export interface RevokePermissionsData {
  userId: string;
  permissions: string[];
}

export interface BulkPermissionsData {
  userIds: string[];
  permissions: string[];
  operation: "grant" | "revoke" | "set";
}

export interface UserActivity {
  id: string;
  action: string;
  entityType: string;
  entityId?: string;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
  details?: any;
}

export interface UserStats {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  usersWithPermission: {
    permission: string;
    count: number;
  }[];
  recentlyCreatedUsers: number;
  recentlyActiveUsers: number;
}

export interface UserOutlet {
  id: string;
  userId: string;
  outletId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  outlet: {
    id: string;
    name: string;
    code?: string;
    address?: string;
    isActive: boolean;
    store?: {
      id: string;
      name: string;
    };
  };
}

export interface BulkAssignOutletsData {
  userId: string;
  storeId: string;
  outletIds: string[];
}

export interface AvailablePermission {
  code: string;
  name: string;
  description: string;
  module: string;
  risk: "low" | "medium" | "high";
}

// Create a dynamic base query for user API
const dynamicUserBaseQuery: BaseQueryFn = async (args, api, extraOptions) => {
  const config = getApiConfig();

  const baseQuery = fetchBaseQuery({
    baseUrl: config.API_URL,
    prepareHeaders: (headers, { getState }) => {
      let token = (getState() as any).auth.token;
      if (!token) {
        token = localStorage.getItem("token");
      }
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      headers.set("accept-language", getRequestLanguage());
      return headers;
    },
  });

  return baseQuery(args, api, extraOptions);
};

export const userApi = createApi({
  reducerPath: "userApi",
  baseQuery: dynamicUserBaseQuery,
  tagTypes: ["User", "UserStats", "Permissions", "UserActivity"],
  endpoints: builder => ({
    // Get user stats
    getUserStats: builder.query<ApiResponse<UserStats>, { storeId?: string } | void>({
      query: (params) => {
        if (params && params.storeId) {
          return `/users/stats?storeId=${params.storeId}`;
        }
        return "/users/stats";
      },
      providesTags: ["UserStats"],
    }),

    // Get all users with filtering and pagination
    getUsers: builder.query<ApiResponse<BackendPaginatedResponse<User>>, UsersQueryParams>({
      query: params => {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            searchParams.append(key, value.toString());
          }
        });
        return `/users?${searchParams.toString()}`;
      },
      transformResponse: (response: any) => {
        // Normalize backend field `lastLogin` -> frontend `lastLoginAt`
        const items = Array.isArray(response?.data?.items)
          ? response.data.items.map((u: any) => ({
            ...u,
            lastLoginAt: u.lastLogin ?? u.lastLoginAt ?? undefined,
          }))
          : response?.data?.items;
        return {
          success: response?.success,
          data: {
            items,
            pagination: response?.data?.pagination,
          },
          message: response?.message,
        } as ApiResponse<BackendPaginatedResponse<User>>;
      },
      providesTags: result =>
        result && result.data?.items && Array.isArray(result.data.items)
          ? [...result.data.items.map(({ id }) => ({ type: "User" as const, id })), "User"]
          : ["User"],
    }),

    // Get single user
    getUser: builder.query<ApiResponse<User>, string>({
      query: id => `/users/${id}`,
      transformResponse: (response: any) => {
        const user = response?.data
          ? { ...response.data, lastLoginAt: response.data.lastLogin ?? response.data.lastLoginAt }
          : response?.data;
        return {
          success: response?.success,
          data: user,
          message: response?.message,
        } as ApiResponse<User>;
      },
      providesTags: (_result, _error, id) => [{ type: "User", id }],
    }),

    // Get user activity
    getUserActivity: builder.query<
      ApiResponse<BackendPaginatedResponse<UserActivity>>,
      { userId: string; page?: number; limit?: number }
    >({
      query: ({ userId, page = 1, limit = 20 }) => ({
        url: `/users/${userId}/activity`,
        params: { page, limit },
      }),
      providesTags: (_result, _error, { userId }) => [{ type: "UserActivity", id: userId }],
    }),

    // Get available permissions
    getAvailablePermissions: builder.query<ApiResponse<AvailablePermission[]>, void>({
      query: () => "/users/permissions/available",
      providesTags: ["Permissions"],
    }),

    // Sync baseline permissions (admin)
    syncPermissions: builder.mutation<
      ApiResponse<{ upserted: number; total: number; grantedToSuperadmin: number }>,
      void
    >({
      query: () => ({
        url: "/permissions/sync",
        method: "POST",
      }),
      invalidatesTags: ["Permissions", "User"],
    }),

    // Create new user
    createUser: builder.mutation<ApiResponse<User>, CreateUserData>({
      query: data => ({
        url: "/users",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["User", "UserStats"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(subscriptionApi.util.invalidateTags(["Subscription"]));
        } catch {
          // Mutation errors are handled by the caller
        }
      },
    }),

    // Update user
    updateUser: builder.mutation<ApiResponse<User>, { id: string; data: UpdateUserData }>({
      query: ({ id, data }) => ({
        url: `/users/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "User", id }, "User", "UserStats"],
    }),

    // Update password
    updatePassword: builder.mutation<ApiResponse<void>, UpdatePasswordData>({
      query: ({ userId, newPassword }) => ({
        url: `/users/${userId}/password`,
        method: "PUT",
        body: { newPassword },
      }),
    }),

    // Delete user
    deleteUser: builder.mutation<ApiResponse<void>, string>({
      query: id => ({
        url: `/users/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [{ type: "User", id }, "User", "UserStats"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(subscriptionApi.util.invalidateTags(["Subscription"]));
        } catch {
          // Mutation errors are handled by the caller
        }
      },
    }),

    // Grant permissions
    grantPermissions: builder.mutation<ApiResponse<User>, GrantPermissionsData>({
      query: ({ userId, permissions }) => ({
        url: `/users/${userId}/permissions`,
        method: "POST",
        body: { permissions },
      }),
      invalidatesTags: (_result, _error, { userId }) => [{ type: "User", id: userId }],
    }),

    // Revoke permissions
    revokePermissions: builder.mutation<ApiResponse<User>, RevokePermissionsData>({
      query: ({ userId, permissions }) => ({
        url: `/users/${userId}/permissions`,
        method: "DELETE",
        body: { permissions },
      }),
      invalidatesTags: (_result, _error, { userId }) => [{ type: "User", id: userId }],
    }),

    // Bulk operations
    bulkDeleteUsers: builder.mutation<ApiResponse<{ deleted: number }>, string[]>({
      query: ids => ({
        url: "/users/bulk-delete",
        method: "POST",
        body: { ids },
      }),
      invalidatesTags: (_result, _error, ids) => [
        ...ids.map(id => ({ type: "User" as const, id })),
        "User",
        "UserStats",
      ],
    }),

    bulkUpdatePermissions: builder.mutation<ApiResponse<{ updated: number }>, BulkPermissionsData>({
      query: data => ({
        url: "/users/bulk-permissions",
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { userIds }) => [
        ...userIds.map(id => ({ type: "User" as const, id })),
        "User",
      ],
    }),

    bulkUpdateStatus: builder.mutation<
      ApiResponse<{ updated: number }>,
      { userIds: string[]; isActive: boolean }
    >({
      query: data => ({
        url: "/users/bulk-status",
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { userIds }) => [
        ...userIds.map(id => ({ type: "User" as const, id })),
        "User",
        "UserStats",
      ],
    }),

    // Export users (GET /users/bulk/export); the file is returned as a blob
    exportUsers: builder.mutation<
      Blob,
      {
        format: "csv" | "json";
        /** Export only these users; all accessible users when omitted or empty */
        userIds?: string[];
        includePermissions?: boolean;
        activeOnly?: boolean;
        storeId?: string;
      }
    >({
      query: ({ userIds, ...params }) => ({
        url: "/users/bulk/export",
        method: "GET",
        params: {
          ...params,
          ...(userIds && userIds.length > 0 ? { userIds: userIds.join(",") } : {}),
        },
        responseHandler: (response: Response) => response.blob(),
      }),
    }),

    // Import users
    bulkImportUsers: builder.mutation<
      ApiResponse<{
        imported: number;
        failed: number;
        errors: Array<{ row: number; error: string }>;
      }>,
      FormData
    >({
      query: formData => ({
        url: "/users/import",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["User", "UserStats"],
    }),

    // Get import template
    getUserImportTemplate: builder.query<Blob, void>({
      query: () => ({
        url: "/users/import-template",
        responseHandler: (response: Response) => response.blob(),
      }),
      keepUnusedDataFor: 0, // Don't cache blob data
    }),

    // User-Outlet Assignment endpoints
    // Get outlets assigned to a user
    getUserOutlets: builder.query<
      ApiResponse<{
        outlets: UserOutlet[];
        pagination: { page: number; limit: number; total: number; pages: number };
      }>,
      { userId: string; page?: number; limit?: number }
    >({
      query: ({ userId, page = 1, limit = 100 }) => ({
        url: `/users/${userId}/outlets`,
        params: { page, limit },
      }),
      transformResponse: (response: any) => {
        return {
          success: response.success,
          data: {
            outlets: response.data,
            pagination: response.pagination,
          },
          message: response.message,
        };
      },
      providesTags: (_result, _error, { userId }) => [{ type: "User", id: `${userId}-outlets` }],
    }),

    // Bulk assign user to outlets
    bulkAssignUserToOutlets: builder.mutation<
      ApiResponse<{ assigned: number; skipped: number; message: string }>,
      BulkAssignOutletsData
    >({
      query: ({ userId, outletIds, storeId }) => ({
        url: `/users/${userId}/outlets/bulk-assign`,
        method: "POST",
        body: { outletIds, storeId },
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "User", id: userId },
        { type: "User", id: `${userId}-outlets` },
      ],
    }),
  }),
});

export const {
  useGetUserStatsQuery,
  useGetUsersQuery,
  useGetUserQuery,
  useGetUserActivityQuery,
  useGetAvailablePermissionsQuery,
  useSyncPermissionsMutation,
  useCreateUserMutation,
  useUpdateUserMutation,
  useUpdatePasswordMutation,
  useDeleteUserMutation,
  useGrantPermissionsMutation,
  useRevokePermissionsMutation,
  useBulkDeleteUsersMutation,
  useBulkUpdatePermissionsMutation,
  useBulkUpdateStatusMutation,
  useExportUsersMutation,
  useBulkImportUsersMutation,
  useGetUserImportTemplateQuery,
  // User-Outlet assignment hooks
  useGetUserOutletsQuery,
  useBulkAssignUserToOutletsMutation,
} = userApi;
